import React, { useState, useRef, useEffect } from "react";
import { IoClose } from "react-icons/io5";
import axios from "axios";
import baseUrl from "../../store/constants/baseUrl";
import { useToaster } from "../Toaster";

const InstructionsBottomSheet = ({
  isOpen,
  onClose,
  locationType,
  locationIndex,
  instructions,
  files,
  onInstructionsChange,
  onFilesChange,
  onSave,
}) => {
  const { showToast } = useToaster();
  const [localInstructions, setLocalInstructions] = useState(instructions || "");
  const [localFiles, setLocalFiles] = useState(files || []);
  const [warnings, setWarnings] = useState([]);
  const [isUploading, setIsUploading] = useState(false);

  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setLocalInstructions(instructions || "");
      setLocalFiles(Array.isArray(files) ? files : []);
    }
  }, [isOpen, instructions, files, locationType, locationIndex]);

  const handleInstructionsChange = (e) => {
    const value = e.target.value;
    setLocalInstructions(value);
    if (onInstructionsChange) onInstructionsChange(value);
  };

  const handleFileUpload = (event) => {
    const uploadedFiles = Array.from(event.target.files);
    const newFiles = uploadedFiles.map((file) => ({
      id: Date.now() + Math.random(),
      name: file.name,
      file,
      type: file.type.startsWith("image/") ? "image" : "document",
      section: locationType,
      sectionIndex: typeof locationIndex === "number" ? locationIndex : null,
    }));

    const updatedFiles = [...localFiles, ...newFiles];
    setLocalFiles(updatedFiles);
    if (onFilesChange) onFilesChange(updatedFiles);
  };

  const handleTakePhoto = (event) => {
    const photoFiles = Array.from(event.target.files);
    const newFiles = photoFiles.map((file) => ({
      id: Date.now() + Math.random(),
      name: `Photo_${new Date().toLocaleDateString()}.jpg`,
      file,
      type: "image",
      section: locationType,
      sectionIndex: typeof locationIndex === "number" ? locationIndex : null,
    }));

    const updatedFiles = [...localFiles, ...newFiles];
    setLocalFiles(updatedFiles);
    if (onFilesChange) onFilesChange(updatedFiles);
  };

  const removeFile = (fileId) => {
    const updatedFiles = localFiles.filter((file) => file.id !== fileId);
    setLocalFiles(updatedFiles);
    if (onFilesChange) onFilesChange(updatedFiles);
  };

  const handleSave = () => {

    (async () => {
      setIsUploading(true);

      try {
        const storageKey = "uploadedFilesMeta";

        const readUploaded = () => {
          try {
            const raw = localStorage.getItem(storageKey);
            return raw ? JSON.parse(raw) : [];
          } catch {
            return [];
          }
        };

        const writeUploaded = (arr) => {
          try {
            localStorage.setItem(storageKey, JSON.stringify(arr));
          } catch (e) {
            console.warn("Failed to persist uploaded meta", e);
          }
        };

        const existing = readUploaded();

        const unique = [];
        const duplicates = [];

        localFiles.forEach((f) => {
          const match = existing.some((m) => {
            return (
              m.name === f.name &&
              m.size === f.file.size &&
              m.type === f.type &&
              m.section === f.section &&
              (m.sectionIndex ?? null) === (f.sectionIndex ?? null)
            );
          });
          if (match) duplicates.push(f);
          else unique.push(f);
        });

        if (duplicates.length > 0) {
          duplicates.forEach((d) =>
            console.log("Duplicate skipped:", {
              name: d.name,
              section: d.section,
              sectionIndex: d.sectionIndex,
            })
          );
        }

        if (unique.length > 0) {
          const bookingId = Number(localStorage.getItem("bookingId")) || 0;
          const visionId = Number(localStorage.getItem("visionReqId")) || 0;

          const promises = unique.map((u) => {
            const fd = new FormData();
            fd.append("BookingId", bookingId);
            fd.append("VisionId", visionId);
            fd.append("Type", u.type || "document");
            fd.append("FileFor", u.section === "stop" ? "stop" : u.section);
            fd.append("File", u.file, u.name);
            return axios.post(`${baseUrl}BookingFile/CreateBookingFile`, fd, {
              headers: { "Content-Type": "multipart/form-data" },
            });
          });

          // Await uploads safely
          await Promise.all(promises);

          // persist metadata
          const toPersist = unique.map((u) => ({
            name: u.name,
            size: u.file.size,
            type: u.type,
            section: u.section,
            sectionIndex: typeof u.sectionIndex === "number" ? u.sectionIndex : null,
          }));
          writeUploaded([...existing, ...toPersist]);

          if (fileInputRef.current) fileInputRef.current.value = null;
        }

        // Always call parent save
        if (onSave) await onSave(localInstructions, localFiles);

        onClose();
      } catch (error) {
        console.error("Upload error", error);
        showToast({
          type: "error",
          message: "We couldn't upload files right now. Please try again.",
          duration: 4500,
        });
        setWarnings((w) => ["Upload failed, please try again.", ...w]);
        onClose();
      } finally {
        setIsUploading(false); // ensures button never stuck
      }
    })();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end justify-center z-50 font-instrument">
      <div className="w-full bg-[#FFFFFF] h-[90%] rounded-t-xl px-7 md:px-[33px] py-[24px] shadow-xl overflow-y-auto pb-28 md:pb-28">
        {/* Header */}
        <div className="flex flex-col items-start justify-between gap-y-[28px]">
          <button onClick={onClose}>
            <img src="/Vision Agent Icons/X Icon/XLightpurple.svg" alt="Close" />
          </button>
          <h3 className="text-[16px] font-semibold text-[#211F59]">
            Share extra details to help make your move smoother.
          </h3>
        </div>

        {/* Instructions Text Area */}
        <div className="mt-4">
          <textarea
            className="w-full min-h-[193px] bg-[#E6E6E6] resize-none rounded-[12px] border py-2 px-3 text-[16px] text-[#211F59] placeholder-[#37355980]/50 focus:outline-none focus:border-[#4743BF] focus:ring-1 focus:ring-[#4743BF] transition"
            placeholder="Invoice number, work order number, gate code, parking instructions, fragile items, or anything else the movers should know…"
            value={localInstructions}
            onChange={handleInstructionsChange}
            rows={4}
          />
        </div>

        {localFiles.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-x-[6px] gap-y-[6px] max-w-[328px] md:max-w-full">
            {localFiles.map((file) => (
              <div
                key={file.id}
                className="flex items-center rounded-[10px] p-2 gap-x-1 max-w-full"
                style={{ backgroundColor: "#7876A1" }}
              >
                <img src="/images/NewUiIcons/Vector43.png" alt="attach icon" />
                <span className="text-[12px] truncate" style={{ color: "#F6F6F6" }}>
                  {file.name}
                </span>
                <button
                  onClick={() => removeFile(file.id)}
                  className="ml-[6px] p-[2px] rounded hover:bg-gray-200/20"
                >
                  <IoClose size={12} style={{ color: "#F6F6F6" }} />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* File Upload Section */}
        <div className="mt-[12.45px]">
          <h4 className="text-[16px] font-semibold text-[#211F59]">
            Upload or take photos of receipts, loading areas, or other key details.
          </h4>

          <div className="flex items-start flex-col gap-y-5 mt-[30px] w-full text-[#7876A1] font-semibold text-[16px] leading-[100%]">
            <button className="flex items-end gap-x-2" onClick={() => fileInputRef.current?.click()}>
              <img src="/images/NewUiIcons/Uploadfiles.svg" alt="upload" />
              <span>Upload from camera roll or files</span>
            </button>

            <div className="h-[1px] bg-[#CDCCD7] w-full"></div>

            <button className="flex items-end gap-2 text-sm" onClick={() => cameraInputRef.current?.click()}>
              <img src="/Vision Agent Icons/Camera Icon/Camera Icon-Small Light Purple.svg" alt="camera" />
              <span>Take Pictures</span>
            </button>
          </div>

          <input ref={fileInputRef} type="file" multiple accept="image/*,.pdf,.doc,.docx" onChange={handleFileUpload} style={{ display: "none" }} />
          <input ref={cameraInputRef} type="file" multiple accept="image/*" capture="environment" onChange={handleTakePhoto} style={{ display: "none" }} />
        </div>

        <div className="fixed bottom-[0px] left-0 w-full p-4 z-50 text-[16px] text-[#F6F6FD] font-semibold">
          <button
            onClick={handleSave}
            disabled={isUploading || (!localInstructions.trim() && localFiles.length === 0)}
            className={`${isUploading || (!localInstructions.trim() && localFiles.length === 0)
              ? "bg-gray-200 text-gray-400"
              : "bg-gradient-to-t from-[#4743BF] to-[#211F59] text-white"} p-3 rounded-[8px] w-full`}
          >
            {isUploading ? "Saving..." : "Save & Continue"}{" "}
            <img src="/images/NewUiIcons/Group85.svg" alt="arrow" className="inline ml-2" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default InstructionsBottomSheet;
